<template>
  <article class="v2-profile-tab">
    <header class="v2-profile-tab__header">
      <div class="v2-profile-tab__header-text">
        <SectionHeader
          title="Datos del Directorio"
          eyebrow="Mi Perfil"
          description="Únicamente serán los datos de este apartado los que se muestren en tu perfil público dentro del directorio de miembros/empresas"
          size="display-md"
        />
      </div>
      <div class="v2-profile-tab__actions">
        <BaseButton
          v-if="!editing"
          variant="amber"
          size="md"
          @click="onEdit"
        >
          <template #icon-left>
            <v-icon size="16">mdi-pencil-outline</v-icon>
          </template>
          Editar
        </BaseButton>
        <template v-else>
          <BaseButton
            variant="ghost"
            size="sm"
            :disabled="saving"
            @click="onCancel"
          >
            Cancelar
          </BaseButton>
          <BaseButton
            variant="primary"
            size="sm"
            :loading="saving"
            :disabled="saving"
            @click="onSave"
          >
            <template #icon-left>
              <v-icon size="16">mdi-content-save</v-icon>
            </template>
            Guardar
          </BaseButton>
        </template>
      </div>
    </header>

    <div class="v2-profile-tab__logo">
      <div class="v2-profile-tab__logo-preview">
        <img
          v-if="previewLogoUrl"
          :src="previewLogoUrl"
          alt="Logotipo del directorio"
          class="v2-profile-tab__logo-img"
        />
        <div v-else class="v2-profile-tab__logo-placeholder">
          <v-icon size="36" color="grey lighten-1">mdi-image-outline</v-icon>
          <span class="v2-body v2-body--sm v2-body--muted">Sin logotipo</span>
        </div>
      </div>

      <div class="v2-profile-tab__logo-info">
        <p class="v2-body v2-body--md v2-profile-tab__logo-title">
          Logotipo de la empresa
        </p>
        <p class="v2-body v2-body--sm v2-body--muted">
          Sube una imagen en formato PNG o JPG. Tamaño recomendado 400×400 px.
        </p>
        <div class="v2-profile-tab__logo-actions">
          <BaseButton
            v-if="editing"
            variant="primary"
            size="sm"
            @click="triggerFileInput"
          >
            <template #icon-left>
              <v-icon size="18">mdi-upload</v-icon>
            </template>
            {{ previewLogoUrl ? "Cambiar imagen" : "Subir imagen" }}
          </BaseButton>
          <BaseButton
            v-if="editing && hasLogo"
            variant="ghost"
            size="sm"
            @click="clearLogo"
          >
            <template #icon-left>
              <v-icon size="18">mdi-delete-outline</v-icon>
            </template>
            Quitar
          </BaseButton>
        </div>
        <input
          ref="fileInput"
          type="file"
          accept=".png,.jpg,.jpeg"
          class="v2-profile-tab__file-input"
          @change="onFileSelected"
        />
      </div>
    </div>

    <div class="v2-profile-tab__grid">
      <label class="v2-profile-tab__toggle">
        <input
          type="checkbox"
          :checked="form.visible"
          :disabled="!editing"
          @change="onChange('visible', $event.target.checked)"
        />
        <span class="v2-body v2-body--md">
          Ser visible en el directorio público
        </span>
      </label>

      <label class="v2-profile-tab__toggle">
        <input
          type="checkbox"
          :checked="useMemberData"
          :disabled="!editing"
          @change="onUseMemberDataToggle($event.target.checked)"
        />
        <span class="v2-body v2-body--md">
          Usar datos de miembro titular
        </span>
      </label>
    </div>

    <div class="v2-profile-tab__grid">
      <div class="v2-profile-tab__cell">
        <BaseInput
          :value="form.company"
          label="Nombre Comercial"
          :disabled="!editing"
          :error="errors.company || ''"
          @input="onChange('company', $event)"
        />
      </div>
      <div class="v2-profile-tab__cell">
        <BaseInput
          :value="form.name"
          label="Nombre"
          :disabled="!editing"
          :error="errors.name || ''"
          @input="onChange('name', $event)"
        />
      </div>
      <div class="v2-profile-tab__cell v2-profile-tab__cell--full">
        <label class="v2-profile-tab__label">
          Descripción de la empresa
        </label>
        <BaseTextarea
          :value="form.description"
          :disabled="!editing"
          :rows="4"
          :placeholder="'Describe brevemente la empresa, sus servicios y diferenciadores.'"
          @input="onChange('description', $event)"
        />
      </div>
      <div class="v2-profile-tab__cell">
        <BaseInput
          :value="form.email"
          label="Correo electrónico comercial (opcional)"
          type="email"
          :disabled="!editing"
          :error="errors.email || ''"
          @input="onChange('email', $event)"
        />
      </div>
      <div class="v2-profile-tab__cell">
        <BaseInput
          :value="form.phone"
          label="Teléfono comercial (opcional)"
          type="tel"
          :disabled="!editing"
          @input="onChange('phone', $event)"
        />
      </div>
      <div class="v2-profile-tab__cell">
        <BaseInput
          :value="form.url"
          label="URL (link comercial o profesional)"
          type="url"
          :disabled="!editing"
          :error="errors.url || ''"
          @input="onChange('url', $event)"
        />
      </div>
    </div>
  </article>
</template>

<script>
import Vue from "vue";
import { mapActions, mapMutations } from "vuex";
import SectionHeader from "@/components/memberDashboardV2/ui/SectionHeader.vue";
import BaseInput from "@/components/memberDashboardV2/ui/BaseInput.vue";
import BaseButton from "@/components/memberDashboardV2/ui/BaseButton.vue";
import BaseTextarea from "@/components/memberDashboardV2/ui/BaseTextarea.vue";

const EMPTY_DIRECTORY_DATA = {
  visible: false,
  company: "",
  name: "",
  description: "",
  email: "",
  phone: "",
  url: "",
  logo: "",
  titular: false,
};

export default {
  name: "DirectoryDataTab",
  components: { SectionHeader, BaseInput, BaseButton, BaseTextarea },
  props: {
    user: { type: Object, default: null },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      editing: false,
      editingForm: { ...EMPTY_DIRECTORY_DATA },
      pendingLogo: null,
      errors: {},
      useMemberData: false,
    };
  },
  computed: {
    persistedData() {
      return (this.user && this.user.directoryData) || EMPTY_DIRECTORY_DATA;
    },
    form() {
      return this.editing ? this.editingForm : this.persistedData;
    },
    hasLogo() {
      if (this.editing) {
        return Boolean(this.editingForm.logo);
      }
      return Boolean(this.persistedData.logo);
    },
    previewLogoUrl() {
      if (this.pendingLogo) return this.pendingLogo;
      const logo = (this.editing ? this.editingForm.logo : this.persistedData.logo) || "";
      if (!logo || logo === "NOT_ASSIGNED") return null;
      return logo;
    },
  },
  methods: {
    ...mapMutations("notification", ["show"]),
    ...mapActions("user", ["fetchProfileData"]),
    onEdit() {
      this.editingForm = {
        ...EMPTY_DIRECTORY_DATA,
        ...this.persistedData,
      };
      this.useMemberData = Boolean(this.editingForm.titular);
      if (this.user && this.user.name && !this.editingForm.name) {
        this.editingForm.name = [this.user.name, this.user.firstLastname]
          .filter(Boolean)
          .join(" ")
          .trim();
      }
      this.editing = true;
      this.errors = {};
      this.pendingLogo = null;
    },
    onCancel() {
      this.editing = false;
      this.editingForm = { ...EMPTY_DIRECTORY_DATA };
      this.useMemberData = false;
      this.errors = {};
      this.pendingLogo = null;
    },
    onUseMemberDataToggle(checked) {
      if (!this.editing) return;
      this.useMemberData = checked;
      this.editingForm = {
        ...this.editingForm,
        titular: checked,
      };
      if (checked && this.user) {
        this.editingForm.name = [this.user.name, this.user.firstLastname]
          .filter(Boolean)
          .join(" ")
          .trim();
        this.editingForm.email = this.user.email || "";
        this.editingForm.phone = this.user.phone || "";
        this.editingForm.logo = this.user.profilePicture || "";
        this.editingForm.company = this.user.company || "";
        this.editingForm.url = this.user.url || "";
        this.pendingLogo = null;
      }
    },
    onChange(key, value) {
      if (!this.editing) return;
      this.editingForm = { ...this.editingForm, [key]: value };
    },
    triggerFileInput() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    onFileSelected(event) {
      const [file] = event.target.files || [];
      if (!file) return;
      this.pendingLogo = URL.createObjectURL(file);
      this.editingForm = {
        ...this.editingForm,
        logo: file,
      };
    },
    clearLogo() {
      this.pendingLogo = null;
      this.editingForm = { ...this.editingForm, logo: "" };
    },
    async onSave() {
      const validation = this.validate();
      if (!validation.ok) {
        this.errors = validation.errors;
        return;
      }

      const dataToSend = { ...this.editingForm };
      const logoIsFile = dataToSend.logo instanceof File;

      if (!logoIsFile) {
        dataToSend.logo = dataToSend.logo || "";
      } else {
        delete dataToSend.logo;
      }

      try {
        await Vue.axios({
          method: "PUT",
          url: "/users/directory-data",
          data: { directoryData: dataToSend },
        });

        if (logoIsFile) {
          const formData = new FormData();
          formData.append("file", this.editingForm.logo);
          await Vue.axios.put("/users/directory-logo", formData);
        }

        const refreshResult = await this.fetchProfileData();
        if (!refreshResult || refreshResult.status === false) {
          throw new Error("Los datos se guardaron, pero no se pudo actualizar la vista del perfil");
        }
        this.editing = false;
        this.errors = {};
        this.pendingLogo = null;
        this.show({
          text: "Datos del directorio guardados correctamente",
          color: "success",
        });
      } catch (error) {
        this.show({
          text:
            (error && error.response && error.response.data &&
              (error.response.data.error?.message || error.response.data.message)) ||
            "No se pudieron guardar los datos del directorio",
          color: "error",
        });
      }
    },
    validate() {
      const errors = {};
      const data = this.editingForm;
      if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
        errors.email = "Correo electrónico no válido";
      }
      if (data.url && !/^https?:\/\//i.test(data.url)) {
        errors.url = "La URL debe comenzar con http:// o https://";
      }
      return { ok: Object.keys(errors).length === 0, errors };
    },
  },
};
</script>

<style scoped>
.v2-profile-tab {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  padding: var(--space-6);
}

.v2-profile-tab__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  border-bottom: 1px solid var(--color-border);
  padding-bottom: var(--space-3);
  flex-wrap: wrap;
}

.v2-profile-tab__header-text {
  flex: 1 1 auto;
  min-width: 0;
}

.v2-profile-tab__actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
}

.v2-profile-tab__logo {
  display: flex;
  align-items: center;
  gap: var(--space-5);
  padding: var(--space-4);
  background-color: var(--color-neutral-50);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  flex-wrap: wrap;
}

.v2-profile-tab__logo-preview {
  width: 112px;
  height: 112px;
  border-radius: var(--radius-lg);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: var(--shadow-sm);
}

.v2-profile-tab__logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.v2-profile-tab__logo-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-1);
  color: var(--color-text-subtle);
  text-align: center;
}

.v2-profile-tab__logo-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  flex: 1 1 240px;
  min-width: 0;
}

.v2-profile-tab__logo-title {
  margin: 0;
  font-weight: var(--fw-semibold);
  color: var(--color-text);
}

.v2-profile-tab__logo-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.v2-profile-tab__file-input {
  display: none;
}

.v2-profile-tab__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4) var(--space-6);
  background-color: var(--color-neutral-100);
  padding: var(--space-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
}

@media (min-width: 768px) {
  .v2-profile-tab__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .v2-profile-tab__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.v2-profile-tab__cell {
  min-width: 0;
}

.v2-profile-tab__cell--full {
  grid-column: 1 / -1;
}

.v2-profile-tab__label {
  font-family: var(--font-body);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-medium);
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  display: block;
  margin-bottom: var(--space-1);
}

.v2-profile-tab__toggle {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  cursor: pointer;
  user-select: none;
  padding: var(--space-3) var(--space-4);
  background-color: var(--color-neutral-50);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: var(--font-body);
  color: var(--color-text);
}

.v2-profile-tab__toggle input {
  width: 18px;
  height: 18px;
  accent-color: var(--color-amber);
  cursor: pointer;
}

.v2-profile-tab__toggle:has(input:disabled) {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
